/**
 * Die Karte „Vespera ruft an“ — die Ansicht. (06.10.2026)
 *
 * Wann sie kommt und geht, entscheidet lib/anruf-karte.ts; hier steht nur,
 * wie sie aussieht und sich bedienen lässt. Gestaltung: style.css,
 * „Vespera ruft an — die Karte der Seite“.
 *
 * ⚠️ Ein echter, modaler Dialog (`<dialog>` mit showModal): er liegt über
 *    allem (auch über Vollseiten, Menü und Sprechtasten-Fenster), der Rest
 *    der Seite ist so lange nicht bedienbar, Tab bleibt in der Karte.
 *    role="alertdialog" mit Titel und Text als Beschriftung — ein
 *    Screenreader liest beim Öffnen „Vespera ruft an“ und den Satz darunter.
 * ⚠️ Der Fokus geht auf die KARTE, nicht auf „Abheben“: wer gerade tippt und
 *    Enter drückt, während sie erscheint, soll nicht aus Versehen abheben.
 * ⚠️ Escape (und die Zurück-Geste, die der Browser als `cancel` meldet) heißt
 *    „Ablehnen“ — es gibt keinen dritten Ausgang.
 * ⚠️ Jeder Knopf wirkt höchstens einmal; es gibt höchstens EINE Karte.
 */
import type { AnrufKarteHandlungen } from "./anruf-karte.js";
import { zeichen } from "./zeichen.js";

export const ANRUF_KARTE_TITEL = "Vespera ruft an";
export const ANRUF_KARTE_TEXT =
  "Ein Fokus am iPhone hält das Klingeln zurück. Hier kannst du trotzdem abheben.";

let zaehler = 0;

function knopf(art: "abheben" | "ablehnen", text: string): HTMLButtonElement {
  const b = document.createElement("button");
  b.type = "button";
  b.className = `anruf-karte__knopf anruf-karte__knopf--${art}`;
  b.innerHTML = zeichen("hoerer", 22);
  const beschriftung = document.createElement("span");
  beschriftung.textContent = text;
  b.appendChild(beschriftung);
  return b;
}

/** Die Karte zeigen. `schliesse` nimmt sie wieder weg (auch mehrfach gefahrlos). */
export function zeigeAnrufKarte(h: AnrufKarteHandlungen): { schliesse: () => void } {
  // Höchstens eine — eine stehengebliebene (sollte es nie geben) geht zuerst.
  document.querySelectorAll<HTMLDialogElement>("dialog.anruf-karte").forEach((alt) => {
    if (alt.open) alt.close();
    alt.remove();
  });

  const n = ++zaehler;
  const d = document.createElement("dialog");
  d.className = "anruf-karte";
  d.setAttribute("role", "alertdialog");
  d.setAttribute("aria-labelledby", `anruf-karte-titel-${n}`);
  d.setAttribute("aria-describedby", `anruf-karte-text-${n}`);
  d.tabIndex = -1;

  const klingel = document.createElement("div");
  klingel.className = "anruf-karte__klingel";
  klingel.setAttribute("aria-hidden", "true");
  klingel.innerHTML = zeichen("hoerer", 28);

  const titel = document.createElement("h2");
  titel.id = `anruf-karte-titel-${n}`;
  titel.className = "anruf-karte__titel";
  titel.textContent = ANRUF_KARTE_TITEL;

  const text = document.createElement("p");
  text.id = `anruf-karte-text-${n}`;
  text.className = "anruf-karte__text";
  text.textContent = ANRUF_KARTE_TEXT;

  const leiste = document.createElement("div");
  leiste.className = "anruf-karte__leiste";
  const ablehnen = knopf("ablehnen", "Ablehnen");
  const abheben = knopf("abheben", "Abheben");
  leiste.append(ablehnen, abheben);

  d.append(klingel, titel, text, leiste);

  let erledigt = false;
  const einmal =
    (f: () => void) =>
    (): void => {
      if (erledigt) return;
      erledigt = true;
      f();
    };
  ablehnen.addEventListener("click", einmal(h.ablehnen));
  abheben.addEventListener("click", einmal(h.abheben));
  d.addEventListener("cancel", (e) => {
    e.preventDefault();
    einmal(h.ablehnen)();
  });

  document.body.appendChild(d);
  try {
    d.showModal();
  } catch {
    d.setAttribute("open", ""); // ohne Dialog-Unterstützung: wenigstens sichtbar
  }
  d.focus();

  return {
    schliesse: () => {
      erledigt = true;
      if (d.open) {
        try {
          d.close();
        } catch {
          d.removeAttribute("open");
        }
      }
      d.remove();
    },
  };
}
